import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { useIsDesktop } from "utils/useIsDesktop";
import { Image, Button } from "@nextui-org/react";
import { IconSun, IconMoon } from "@tabler/icons-react";
import synkChainScanPageStore from "./store/store.SynkChainScanPage";
import Sidebar from "./components/Sidebar";
import BrowserControls from "./components/BrowserControls";
import ExplorerIframe from "./components/ExplorerIframe";

const SynkChainScanPage: React.FC = observer(() => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    const initialize = async () => {
      const handleResize = () => {
        synkChainScanPageStore.isSidebarOpen = window.innerWidth >= 768;
      };

      handleResize();
      window.addEventListener("resize", handleResize);

      // Initialize store with default chain and explorer
      await synkChainScanPageStore.initialize();

      return () => window.removeEventListener("resize", handleResize);
    };

    initialize();
  }, []);

  return (
    <div
      className={`h-dvh w-screen flex flex-col relative overflow-hidden ${
        synkChainScanPageStore.isDarkMode ? "bg-black" : "bg-white"
      }`}
    >
      <div className="flex h-dvh w-full flex-col overflow-auto">
        <div className="flex flex-1">
          <div className="relative h-full w-full">
            <div className="flex h-dvh w-full flex-col">
              <div className="flex h-dvh w-full flex-col">
                <PanelGroup
                  autoSaveId="conditional"
                  direction="horizontal"
                  className="flex flex-1"
                >
                  {synkChainScanPageStore.isSidebarOpen && (
                    <>
                      <Panel
                        defaultSize={22}
                        minSize={12}
                        maxSize={30}
                        id="left"
                        order={1}
                        className="h-full relative min-w-[200px]"
                      >
                        <div className="pt-4.5 flex h-[52px] w-full flex-row items-center justify-between px-2">
                          <div className="flex items-center gap-x-2">
                            <Image
                              src="images/chainscan2.png"
                              alt="Logo"
                              width={32}
                              height={32}
                              className="object-contain"
                            />
                            <span
                              className={`inline bg-gradient-to-br ${
                                synkChainScanPageStore.isDarkMode
                                  ? "from-foreground-800 to-foreground-500"
                                  : "from-black to-black/60"
                              } bg-clip-text text-sm text-transparent uppercase font-semibold`}
                            >
                              ChainScan
                            </span>
                          </div>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onClick={synkChainScanPageStore.toggleTheme}
                            className={
                              synkChainScanPageStore.isDarkMode
                                ? "text-white"
                                : "text-black"
                            }
                          >
                            {synkChainScanPageStore.isDarkMode ? (
                              <IconSun className="h-4 w-4" />
                            ) : (
                              <IconMoon className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <Sidebar />
                      </Panel>
                      <PanelResizeHandle className="h-[100%] w-1 cursor-col-resize data-[resize-handle-active]:bg-white/20 data-[resize-handle-active]:backdrop-blur-custom hover:bg-white/20 hover:backdrop-blur-custom" />
                    </>
                  )}
                  <Panel
                    id="center"
                    order={2}
                    className="flex h-full w-full flex-col items-center justify-center md:pl-2 md:pr-2"
                  >
                    <div className="relative flex h-full max-h-full w-full flex-col gap-y-1 sm:gap-0">
                      <div className="relative flex h-full max-h-full flex-1 overflow-hidden">
                        <div className="flex h-full w-full">
                          <div className="h-full w-full flex-shrink-0">
                            <div className="relative h-full w-full pt-3 pb-3">
                              <div
                                className={`${
                                  synkChainScanPageStore.isDarkMode
                                    ? "bg-customGray/80"
                                    : "bg-[hsl(240,4.76%,95.88%)]"
                                } rounded-large flex h-full w-full flex-col`}
                              >
                                <BrowserControls
                                  onRefresh={synkChainScanPageStore.refresh}
                                  onFullscreen={
                                    synkChainScanPageStore.toggleFullscreen
                                  }
                                />
                                <ExplorerIframe
                                  src={synkChainScanPageStore.currentUrl}
                                  title="Explorer"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>
                </PanelGroup>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SynkChainScanPage;
